import {
    Row,
    Col,
    Typography,
    Table,
    Button,
    message,
  } from "antd";
  import { routes } from "common/routes";
  import { generateHeaders, getSlotName } from "common/utils";
  import { AppPageHeader } from "components/AppPageHeader";
  import { useAppDispatch } from "hooks/useAppDispatch";
  import { useAppSelector } from "hooks/useAppSelector";
  import { useDocumentTitle } from "hooks/usePageTitle";
  import { useEffect, useMemo } from "react";
  import { useNavigate } from "react-router-dom";
import { cancelProcessingProjectedSr, removeProcessingProjectedSr, saveProcessingProjectedSr } from "store/admin-slice/adminServicesSlice";
  
  const MESSAGE_KEY = "SAVE_REQUISITION";
  
  export const SaveProjectedSrPage = () => {
    useDocumentTitle("Save Projected SR");
    const { processingProjectedSrMapping, status, error } = useAppSelector(
      (state) => state.adminServices
    );
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
  
    useEffect(() => {
      const { timeUploaded } = processingProjectedSrMapping;
      if (timeUploaded) {
        const uploadedTime = new Date(timeUploaded).getTime();
        const currentTime = new Date().getTime();
        const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
  
        if (minsPassed >= 15) {
          dispatch(removeProcessingProjectedSr());
        }
      }
  
      if (!Object.keys(processingProjectedSrMapping).length) { 
        navigate(`/${routes.PROCUREMENT.UPLOAD_PROJECTED_SR}`);
      }
    }, [dispatch, processingProjectedSrMapping, navigate]);
  
    useEffect(() => {
      if (status === "loading") {
        message.loading({ content: "saving upload", key: MESSAGE_KEY });
      } else if (status === "success") {
        message.success({
          content: "Projected Sr saved!",
          duration: 5,
          key: MESSAGE_KEY,
        });
      } else if (status === "cancelling") {
        message.loading({ content: "cancelling upload", key: MESSAGE_KEY });
      } else if (status === "error") {
        message.error({ content: error, key: MESSAGE_KEY });
      }
  
      return () => {
        message.destroy();
      };
    }, [status, error]);
  
    let content;
    const { headerMapping } = processingProjectedSrMapping;
    const { Text } = Typography;
  
    const generateMemoizedHeaders = useMemo(
      () => generateHeaders(headerMapping),
      [headerMapping]
    );
  
    const onSavePpd = () => {
      dispatch(saveProcessingProjectedSr());
    };
  
    const onCancelSave = () => {
      dispatch(cancelProcessingProjectedSr());
    };
  
    if ((processingProjectedSrMapping.success) === 0) {
      content = <Text>Nothing to show here!</Text>;
    } else {
      const headers = generateMemoizedHeaders || {};
      const successRows = processingProjectedSrMapping.successRows;
      const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
      const columns = [
        ...Object.keys(headers).map(
          (key) =>
            ({
              key,
              title: headers[key],
              dataIndex: key,
            } as any)
        ),
      ];
      content = (
        <Table
          size="small"
          columns={columns}
          dataSource={data}
          scroll={{ y: 500, x: "100vh" }}
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={24}>
                  <Row>
                    <Col>
                      <Button
                        type="primary"
                        onClick={onSavePpd}
                        loading={status === "loading"}
                        disabled={status === "loading"}
                      >
                        Save Projected SR
                      </Button>
                    </Col>
                    <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                      <Button
                        onClick={onCancelSave}
                        disabled={status === "loading"}
                      >
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        ></Table>
      );
    }
  
    return (
      <>
        <Row>
          <Col>
            <AppPageHeader title="Save Projected SR" />
          </Col>
        </Row>
        <Row>
          <Col xs={24}>{content}</Col>
        </Row>
      </>
    );
  };
  